/**
 * API 统一调用层
 * 自动根据配置切换 Mock/正式 API
 */

import { config } from '../config';
import http from './axios';
import * as mockApi from './mock';
import {
  FormData,
  LoginResponse,
  SubmitRegistrationResponse,
  Registration,
  CompetitionConfig,
  CaseItem,
  ApiResponse,
} from '../types';

/**
 * 用户登录
 * @param phone 手机号
 * @param studentName 学生姓名
 */
export const login = async (
  phone: string,
  studentName: string
): Promise<ApiResponse<LoginResponse>> => {
  if (config.useMock) {
    return mockApi.mockLogin(phone, studentName);
  }
  return http.post('/api/v1/auth/login', { phone, studentName });
};

/**
 * 提交报名
 * @param data 表单数据
 */
export const submitRegistration = async (
  data: FormData
): Promise<ApiResponse<SubmitRegistrationResponse>> => {
  if (config.useMock) {
    return mockApi.mockSubmitRegistration(data);
  }
  return http.post('/api/v1/registrations', data);
};

/**
 * 根据手机号和姓名查询报名记录
 * @param phone 手机号
 * @param studentName 学生姓名
 */
export const queryRegistration = async (
  phone: string,
  studentName: string
): Promise<ApiResponse<Registration>> => {
  if (config.useMock) {
    return mockApi.mockGetMyRegistration(phone);
  }
  return http.post('/api/v1/registrations/query', { phone, studentName });
};

/**
 * 修改报名信息
 * @param id 报名ID
 * @param data 表单数据
 */
export const updateRegistration = async (
  id: string,
  data: FormData
): Promise<ApiResponse<Registration>> => {
  if (config.useMock) {
    // Mock模式下模拟更新
    return {
      code: 0,
      message: '修改成功',
      data: {
        ...data,
        id,
        status: 'pending',
        statusText: '审核中',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      } as Registration,
    };
  }
  return http.put(`/api/v1/registrations/${id}`, data);
};

/**
 * 根据ID查询报名状态
 * @param id 报名ID
 */
export const getRegistration = async (
  id: string
): Promise<ApiResponse<Registration>> => {
  if (config.useMock) {
    return mockApi.mockGetRegistration(id);
  }
  return http.get(`/api/v1/registrations/${id}`);
};

/**
 * 获取当前用户的报名信息
 * @param phone 手机号（Mock模式需要）
 */
export const getMyRegistration = async (
  phone?: string
): Promise<ApiResponse<Registration>> => {
  if (config.useMock) {
    if (!phone) throw new Error('Mock模式需要提供手机号');
    return mockApi.mockGetMyRegistration(phone);
  }
  return http.get('/api/v1/registrations/me');
};

/**
 * 获取赛事配置
 */
export const getCompetitionConfig = async (): Promise<ApiResponse<CompetitionConfig>> => {
  if (config.useMock) {
    return mockApi.mockGetCompetitionConfig();
  }
  return http.get('/api/v1/config/competition');
};

/**
 * 获取案例列表
 */
export const getCases = async (): Promise<ApiResponse<CaseItem[]>> => {
  if (config.useMock) {
    return mockApi.mockGetCases();
  }
  return http.get('/api/v1/cases');
};

